import { useMemo } from 'react';
import { useSelector, type DefaultRootState } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  appPersistSlice,
  buildAppPrefix,
  useGetDemoSuffix,
  type PersistSliceSuffix,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import {
  DEFAULT_BRAND_ID,
  useGetSelectedBrand,
} from '@bbkAdminRedux/rtkq/brands.slice';
import { playSlice } from '@bbkAdminRedux/rtkq/play.slice';
import { type ReportFilter } from '@bbkAdminRedux/rtkq/reports.slice';
import { type OfferPresentation } from '@bbkAdminUtils/api-client/rest-offers-api';
import { type EnumLike } from '@bbkAdminUtils/utility-types';

export enum ReportsFilterType {
  experiences = 'experiences',
  audiences = 'audiences',
  plays = 'plays',
  predictive_dashboard = 'predictive_dashboard',
  delivery = 'delivery',
}

export type ExperienceFilter = {
  field: 'funnel';
  operator: 'equals';
  value: number;
};

export type AudienceFilter = {
  field: 'funnel_audiences';
  operator: 'equals';
  value: `${number},${string}` | `${number},${string},${string}`;
};

export type PlayFilter = {
  field: 'play';
  operator: 'equals';
  value: string;
};

export type DeliveryFilter = EnumLike<typeof OfferPresentation>;
export type ReportsFilterState = {
  type: ReportsFilterType;
  brandFilter?: string;
  experiencesFilters?: ExperienceFilter[];
  audiencesFilters?: AudienceFilter[];
  playsFilters?: PlayFilter[];
  deliveryFilters?: DeliveryFilter[];
  lastBrandFilter?: string;
};

export const defaultReportFilterState: ReportsFilterState = {
  type: ReportsFilterType.experiences,
};
export const selectReportFilterForPicker =
  (suffix: PersistSliceSuffix = '') =>
  (state: DefaultRootState) => {
    const appKey = selectAppId(state);
    const prefix = buildAppPrefix(appKey, suffix);
    return (
      state[appPersistSlice.name][prefix]?.reportsFilter ??
      defaultReportFilterState
    );
  };

export const selectExperiencesAudiencesPlaysReportFilter = (
  suffix: PersistSliceSuffix = ''
) =>
  createSelector(
    (state: DefaultRootState) => selectReportFilterForPicker(suffix)(state),
    (filter): (ExperienceFilter | AudienceFilter | PlayFilter)[] => {
      if (filter.type === ReportsFilterType.audiences) {
        return filter.audiencesFilters ?? [];
      } else if (filter.type === ReportsFilterType.experiences) {
        return filter.experiencesFilters ?? [];
      } else if (filter.type === ReportsFilterType.plays) {
        return filter.playsFilters ?? [];
      } else {
        return [];
      }
    }
  );

export const selectDeliveryReportFilter = (suffix: PersistSliceSuffix = '') =>
  createSelector(
    (state: DefaultRootState) => selectReportFilterForPicker(suffix)(state),
    (filter) => filter.deliveryFilters ?? []
  );

export const useResolvePlaysFromDeliveryFilter = (): ReportFilter[] => {
  const appKey = useSelector(selectAppId);
  const brandId = useGetSelectedBrand(appKey) ?? DEFAULT_BRAND_ID;

  const suffix = useGetDemoSuffix();
  const filters = useSelector(selectDeliveryReportFilter(suffix));
  const noNeedToFetch = filters.length === 0 || filters.length === 4;
  const { data: playsList = [] } = playSlice.endpoints.listPlays.useQuery(
    {
      appKey,
      brandId,
      context: BBKRequestContext.lifecycle_reports,
    },
    { skip: noNeedToFetch }
  );

  return useMemo(() => {
    if (noNeedToFetch) return [];
    return playsList
      .filter((x) => filters.includes(x.presentation))
      .map((x) => ({
        field: 'play_id',
        operator: 'equals',
        value: x.id,
      }));
  }, [filters, noNeedToFetch, playsList]);
};
